import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import { createSignal } from 'solid-js'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'

const BadgePage: Component = () => {
  const [count, setCount] = createSignal(99)

  return (
    <>
      <Title>Badge Component - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6">
          <h1 class="text-4xl font-bold text-foreground mb-4">Badge</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Pequeñas etiquetas o contadores de estado que se superponen a otros elementos. Indican notificaciones, disponibilidad o resaltan estados específicos de forma compacta.
          </p>
        </div>

        <div class="space-y-12">
          {/* Tipos y Colores */}
          <Card>
            <Card.Header
              title="Colores y Variantes Semánticas"
              subtitle="Una gama completa de colores corporativos para cada estado de la aplicación."
              icon={<Icon icon="material-symbols-light:palette-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="flex flex-col gap-10">
                <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-y-10 items-center justify-items-center p-8 bg-surface-muted rounded-xl border border-border shadow-sm transition-all duration-500">
                  <Badge count={5} color="primary">
                    <Button variant="outlined" size="sm">
                      Primary
                    </Button>
                  </Badge>
                  <Badge count={5} color="secondary">
                    <Button variant="outlined" size="sm">
                      Secondary
                    </Button>
                  </Badge>
                  <Badge count={5} color="success">
                    <Button variant="outlined" size="sm">
                      Success
                    </Button>
                  </Badge>
                  <Badge count={5} color="danger">
                    <Button variant="outlined" size="sm">
                      Danger
                    </Button>
                  </Badge>
                  <Badge count={5} color="warning">
                    <Button variant="outlined" size="sm">
                      Warning
                    </Button>
                  </Badge>
                  <Badge count={5} color="info">
                    <Button variant="outlined" size="sm">
                      Info
                    </Button>
                  </Badge>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div class="p-6 bg-surface rounded-xl border border-border flex flex-col gap-4 shadow-sm group hover:border-primary-500/50 transition-all">
                    <h4 class="font-bold flex items-center gap-2">
                      <Icon icon="material-symbols-light:swap-horiz" size={18} class="text-primary-500" /> Contained
                    </h4>
                    <p class="text-sm text-foreground-subtle">Variante por defecto con color sólido y texto de alto contraste.</p>
                    <div class="flex items-center justify-center p-4">
                      <Badge count={8} variant="contained" color="primary">
                        <div class="w-12 h-12 bg-surface-muted rounded-lg" />
                      </Badge>
                    </div>
                  </div>
                  <div class="p-6 bg-surface rounded-xl border border-border flex flex-col gap-4 shadow-sm group hover:border-info-500/50 transition-all text-balance">
                    <h4 class="font-bold flex items-center gap-2">
                      <Icon icon="material-symbols-light:chevron-right" size={18} class="text-info-500" /> Outlined
                    </h4>
                    <p class="text-sm text-foreground-subtle">Borde colorido y fondo limpio, ideal para interfaces sutiles.</p>
                    <div class="flex items-center justify-center p-4">
                      <Badge count={12} variant="outlined" color="info">
                        <div class="w-12 h-12 bg-surface-muted rounded-lg" />
                      </Badge>
                    </div>
                  </div>
                  <div class="p-6 bg-surface rounded-xl border border-border flex flex-col gap-4 shadow-sm group hover:border-emerald-500/50 transition-all">
                    <h4 class="font-bold flex items-center gap-2">
                      <Icon icon="material-symbols-light:widgets-outline" size={18} class="text-emerald-500" /> Soft
                    </h4>
                    <p class="text-sm text-foreground-subtle">Fondo pastel con texto a juego para un acabado premium.</p>
                    <div class="flex items-center justify-center p-4">
                      <Badge count={99} variant="soft" color="success">
                        <div class="w-12 h-12 bg-surface-muted rounded-lg" />
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Posicionamiento */}
          <Card>
            <Card.Header
              title="Estrategia de Posicionamiento"
              subtitle="Control total sobre dónde anclar la notificación respecto a su contenedor."
              icon={<Icon icon="material-symbols-light:maximize" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-2 md:grid-cols-4 gap-8 p-10 bg-surface-muted rounded-xl border border-border shadow-inner">
                <div class="flex flex-col items-center gap-3">
                  <Badge count={1} position="top-left" color="primary">
                    <div class="w-20 h-20 bg-surface rounded-xl border border-border shadow-sm" />
                  </Badge>
                  <span class="text-[10px] font-bold text-foreground-subtle">top-left</span>
                </div>
                <div class="flex flex-col items-center gap-3">
                  <Badge count={2} position="top-right" color="primary">
                    <div class="w-20 h-20 bg-surface rounded-xl border border-border shadow-sm" />
                  </Badge>
                  <span class="text-[10px] font-bold text-foreground-subtle">top-right</span>
                </div>
                <div class="flex flex-col items-center gap-3">
                  <Badge count={3} position="bottom-left" color="primary">
                    <div class="w-20 h-20 bg-surface rounded-xl border border-border shadow-sm" />
                  </Badge>
                  <span class="text-[10px] font-bold text-foreground-subtle">bottom-left</span>
                </div>
                <div class="flex flex-col items-center gap-3">
                  <Badge count={4} position="bottom-right" color="primary">
                    <div class="w-20 h-20 bg-surface rounded-xl border border-border shadow-sm" />
                  </Badge>
                  <span class="text-[10px] font-bold text-foreground-subtle">bottom-right</span>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Escenarios Avanzados */}
          <Card>
            <Card.Header
              title="Configuraciones Avanzadas"
              subtitle="Cero valores, límites máximos y modo punto minimalista."
              icon={<Icon icon="material-symbols-light:settings-outline" class="text-amber-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div class="p-6 bg-surface-muted rounded-xl space-y-4 border border-border">
                  <h4 class="font-bold flex items-center gap-2">
                    <Icon icon="material-symbols-light:pin-outline" size={18} class="text-primary-500" /> Manejo de Ceros
                  </h4>
                  <p class="text-xs text-foreground-subtle leading-relaxed">Controla si el badge debe ocultarse cuando el conteo es cero.</p>
                  <div class="flex justify-center gap-4 py-2">
                    <Badge count={0} showZero={false}>
                      <div class="w-10 h-10 bg-surface rounded flex items-center justify-center border text-[10px]">Oculto</div>
                    </Badge>
                    <Badge count={0} showZero={true}>
                      <div class="w-10 h-10 bg-surface rounded flex items-center justify-center border text-[10px]">Visible</div>
                    </Badge>
                  </div>
                </div>

                <div class="p-6 bg-surface-muted rounded-xl space-y-4 border border-border">
                  <h4 class="font-bold flex items-center gap-2">
                    <Icon icon="material-symbols-light:maximize" size={18} class="text-danger-500" /> Valor Máximo
                  </h4>
                  <p class="text-xs text-foreground-subtle leading-relaxed">Evita que el badge crezca indefinidamente truncando valores altos (+).</p>
                  <div class="flex justify-center gap-4 py-2">
                    <Badge count={120} max={99}>
                      <Button variant="text" size="sm">
                        99+
                      </Button>
                    </Badge>
                    <Badge count={1500} max={999}>
                      <Button variant="text" size="sm">
                        999+
                      </Button>
                    </Badge>
                  </div>
                </div>

                <div class="p-6 bg-surface-muted rounded-xl space-y-4 border border-border">
                  <h4 class="font-bold flex items-center gap-2">
                    <Icon icon="material-symbols-light:pin-drop-outline" size={18} class="text-success-500" /> Modo Punto (Dot)
                  </h4>
                  <p class="text-xs text-foreground-subtle leading-relaxed">Ideal para indicar estados de disponibilidad sin números ruidosos.</p>
                  <div class="flex justify-center gap-6 py-2">
                    <Badge dot color="success" size="lg">
                      <Icon icon="material-symbols-light:person-outline" size={28} class="text-foreground-subtle" />
                    </Badge>
                    <Badge dot color="danger" size="md">
                      <Icon icon="material-symbols-light:notifications-outline" size={28} class="text-foreground-subtle" />
                    </Badge>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Ejemplo Dinámico */}
          <Card>
            <Card.Header
              title="Consola de Notificaciones"
              subtitle="Prueba la interactividad sincronizando el estado con variables externas."
              icon={<Icon icon="material-symbols-light:videogame-asset-outline" class="text-indigo-500" size={24} />}
            />
            <Card.Body>
              <div class="flex flex-col md:flex-row items-center justify-center gap-12 p-10 bg-surface-muted rounded-xl border border-border">
                <Badge count={count()} color="primary" size="lg" max={99}>
                  <div class="p-6 bg-surface rounded-full shadow-lg">
                    <Icon icon="material-symbols-light:inbox-outline" size={32} class="text-primary-600" />
                  </div>
                </Badge>

                <div class="flex gap-4">
                  <Button variant="outlined" color="secondary" onClick={() => setCount(prev => Math.max(0, prev - 1))} startIcon="material-symbols-light:indeterminate-check-box-outline">
                    Reducir
                  </Button>
                  <Button variant="contained" color="primary" onClick={() => setCount(prev => prev + 1)} startIcon="material-symbols-light:add-box-outline">
                    Incrementar
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header title="API Reference" subtitle="Manual técnico de las propiedades de Badge." icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />} />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b border-border">Propiedad</th>
                      <th class="px-6 py-4 border-b border-border">Tipo</th>
                      <th class="px-6 py-4 border-b border-border">Default</th>
                      <th class="px-6 py-4 border-b border-border">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">count</td>
                      <td class="px-6 py-4 text-foreground-subtle">number</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 text-foreground-muted">Valor numérico a mostrar.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">color</td>
                      <td class="px-6 py-4 text-foreground-subtle">BadgeColorType</td>
                      <td class="px-6 py-4 font-mono">'primary'</td>
                      <td class="px-6 py-4 text-foreground-muted">Paleta de color del indicador.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">variant</td>
                      <td class="px-6 py-4 text-foreground-subtle">BadgeVariantType</td>
                      <td class="px-6 py-4 font-mono">'contained'</td>
                      <td class="px-6 py-4 text-foreground-muted">Estilo visual (relleno, borde o suave).</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">position</td>
                      <td class="px-6 py-4 text-foreground-subtle">BadgePositionType</td>
                      <td class="px-6 py-4 font-mono">'top-right'</td>
                      <td class="px-6 py-4 text-foreground-muted">Esquina donde se anclará el badge.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">max</td>
                      <td class="px-6 py-4 text-foreground-subtle">number</td>
                      <td class="px-6 py-4 font-mono">99</td>
                      <td class="px-6 py-4 text-foreground-muted">Valor máximo antes de mostrar '+'.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">dot</td>
                      <td class="px-6 py-4 text-foreground-subtle">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4 text-foreground-muted">Si es true, muestra un punto pequeño sin número.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default BadgePage
